import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

import { getPhysicalBatteries, patchSubscriptionPhysicalBattery } from '@/api/subscription.api';
import { useSubscriptionStore } from '@/stores/index';

import type {
  MonthlyDiscountPerkWh,
  PatchSubscriptionPhysicalBatteryRequest,
  PhysicalBattery,
} from '@/types/subscription.type';

const usePhysicalBatteryStore = defineStore('physical-battery', () => {
  const subscriptionStore = useSubscriptionStore();

  const physicalBatteries: Ref<PhysicalBattery[]> = ref([]);
  const minimumPhysicalBatteryCapacityInkWh: Ref<number | null> = ref(null);
  const monthlyDiscountPerkWh: Ref<MonthlyDiscountPerkWh | null> = ref(null);

  const isLoadingPhysicalBatteries = ref<boolean>(false);
  const isLoadingUpdatePhysicalBattery = ref<boolean>(false);

  const findPhysicalBatteries = async (): Promise<void> => {
    isLoadingPhysicalBatteries.value = true;
    try {
      const response = await getPhysicalBatteries();
      physicalBatteries.value = response.physicalBatteries;
      minimumPhysicalBatteryCapacityInkWh.value = response.minimumPhysicalBatteryCapacityInkWh;
      monthlyDiscountPerkWh.value = response.monthlyDiscountPerkWh;
    } catch (error) {
      physicalBatteries.value = [];
      minimumPhysicalBatteryCapacityInkWh.value = null;
      monthlyDiscountPerkWh.value = null;
      throw error;
    } finally {
      isLoadingPhysicalBatteries.value = false;
    }
  };

  const updatePhysicalBattery = async (
    payload: PatchSubscriptionPhysicalBatteryRequest
  ): Promise<void> => {
    isLoadingUpdatePhysicalBattery.value = true;
    try {
      const subscription = await patchSubscriptionPhysicalBattery(
        subscriptionStore.getCurrentSubscriptionId,
        payload
      );
      subscriptionStore.setSubscriptionData(subscription);
    } finally {
      isLoadingUpdatePhysicalBattery.value = false;
    }
  };

  return {
    physicalBatteries,
    minimumPhysicalBatteryCapacityInkWh,
    monthlyDiscountPerkWh,
    isLoadingPhysicalBatteries,
    isLoadingUpdatePhysicalBattery,
    findPhysicalBatteries,
    updatePhysicalBattery,
  };
});

export default usePhysicalBatteryStore;
